import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { footerLabels, termsLabels } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect } from "react";
import { useNativeExpandableSheetProps } from "@/lib/native/useNativeExpandableSheetProps";
import { cn } from "@/lib/utils";

type TabValue =
  | "general"
  | "buyer-b2b"
  | "buyer-consumer"
  | "seller-business"
  | "seller-consumer"
  | "complaint"
  | "pricing";

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: TabValue;
  visibleTabs?: TabValue[];
  highlightTabs?: TabValue[];
  /**
   * When true, the dialog is used in the "accept general terms" flow.
   * It will only show the General Terms tab unless `visibleTabs` is explicitly provided.
   */
  generalTermsAccepting?: boolean;
}

const formatBold = (s: string) =>
  s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

type TermsTabData = {
  title?: string;
  lastUpdatedDate?: string;
  intro?: string;
  intro2?: string;
  sections?: Record<string, Record<string, unknown>>;
};

function TermsTabFromContent({
  data,
  lastUpdated,
  fallbackTitle,
}: {
  data: TermsTabData | undefined;
  lastUpdated: string;
  fallbackTitle: string;
}) {
  if (!data) return null;
  const sections = data.sections ? Object.values(data.sections) : [];
  const dateLabel = data.lastUpdatedDate
    ? lastUpdated + ": " + data.lastUpdatedDate
    : lastUpdated;
  return (
    <div className="space-y-4 text-xs pr-2">
      <div>
        <h2 className="text-sm font-semibold mb-2">
          {data.title || fallbackTitle}
        </h2>
        <p className="text-muted-foreground mb-4">{dateLabel}</p>
        {data.intro && (
          <p>
            <span
              dangerouslySetInnerHTML={{ __html: formatBold(data.intro) }}
            />
          </p>
        )}
        {data.intro2 && (
          <p className="mt-2">
            <span
              dangerouslySetInnerHTML={{ __html: formatBold(data.intro2) }}
            />
          </p>
        )}
        {sections.map((section, i) => (
          <TermsSection key={i} section={section} fallback={null} />
        ))}
      </div>
    </div>
  );
}

const FEE_SECTION_KEYS = ["buyerFee", "sellerFeePrivate", "sellerFeeDealer"];

function StyledTermsTabContent({
  data,
  lastUpdated,
  fallbackTitle,
  accentFeeSections = false,
}: {
  data: TermsTabData | undefined;
  lastUpdated: string;
  fallbackTitle: string;
  accentFeeSections?: boolean;
}) {
  if (!data) return null;
  const sections = data.sections
    ? (Object.entries(data.sections) as [string, Record<string, unknown>][])
    : [];
  const dateLabel = data.lastUpdatedDate
    ? lastUpdated + ": " + data.lastUpdatedDate
    : lastUpdated;
  return (
    <div className="space-y-5 text-xs pr-2">
      <header className="space-y-1">
        <h2 className="text-base font-semibold tracking-tight">
          {data.title || fallbackTitle}
        </h2>
        <p className="text-muted-foreground text-[11px]">{dateLabel}</p>
        {data.intro && (
          <p className="text-muted-foreground">
            <span
              dangerouslySetInnerHTML={{ __html: formatBold(data.intro) }}
            />
          </p>
        )}
        {data.intro2 && (
          <p className="mt-2 text-muted-foreground/90 leading-relaxed">
            <span
              dangerouslySetInnerHTML={{ __html: formatBold(data.intro2) }}
            />
          </p>
        )}
      </header>
      <div className="space-y-4">
        {sections.map(([key, section]) => {
          const isFeeSection =
            accentFeeSections && FEE_SECTION_KEYS.includes(key);
          return (
            <article
              key={key}
              className={
                isFeeSection
                  ? "rounded-xl border bg-card p-4 sm:p-5 shadow-sm border-l-4 border-l-primary/60"
                  : "rounded-xl border bg-muted/30 p-4 sm:p-5"
              }
            >
              <TermsSection section={section} fallback={null} variant="card" />
            </article>
          );
        })}
      </div>
    </div>
  );
}

function PricingTabContent({
  data,
  lastUpdated,
  fallbackTitle,
}: {
  data: TermsTabData | undefined;
  lastUpdated: string;
  fallbackTitle: string;
}) {
  return (
    <StyledTermsTabContent
      data={data}
      lastUpdated={lastUpdated}
      fallbackTitle={fallbackTitle}
      accentFeeSections
    />
  );
}

function TermsSection({
  section,
  fallback,
  variant = "default",
}: {
  section: Record<string, unknown> | undefined;
  fallback: React.ReactNode;
  variant?: "default" | "card";
}) {
  if (!section?.title) return <>{fallback}</>;
  const title = section.title as string;
  const rest: Record<string, unknown> = { ...section };
  delete rest.title;
  const entries = Object.entries(rest).sort(([a], [b]) => {
    const aBase = a.replace(/_list$/, "").replace(/^ol_/, "");
    const bBase = b.replace(/_list$/, "").replace(/^ol_/, "");
    // Keep "8.1" above its following bullet list ("8.1_list" / orphan "ol_list")
    if (aBase === bBase) return a.endsWith("_list") ? 1 : -1;
    const aNum = /^\d+\.\d+$/.test(a);
    const bNum = /^\d+\.\d+$/.test(b);
    const aList = a === "ol_list" || a === "list" || a.endsWith("_list");
    const bList = b === "ol_list" || b === "list" || b.endsWith("_list");
    if (aNum && bList) return -1;
    if (bNum && aList) return 1;
    const order: Record<string, number> = {
      p1: 0,
      ol_list: 1,
      list: 2,
      p2: 3,
      p3: 4,
    };
    const oa = order[a] ?? 99;
    const ob = order[b] ?? 99;
    if (oa !== 99 || ob !== 99) return oa - ob;
    return a.localeCompare(b);
  });
  const wrapperClass =
    variant === "card"
      ? "pt-0 mt-0"
      : "border-t pt-4 mt-4";
  return (
    <div className={wrapperClass}>
      <h3 className={variant === "card" ? "text-sm font-semibold mb-3 text-foreground" : "text-xs font-semibold mb-2"}>{title}</h3>
      <div className={variant === "card" ? "space-y-3 ml-0" : "space-y-2 ml-4"}>
        {entries.map(([key, val]) => {
          if (key.endsWith("_list") && Array.isArray(val)) {
            return (
              <ul key={key} className="list-disc pl-6 space-y-1">
                {val.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            );
          }
          if (key === "list" && Array.isArray(val)) {
            return (
              <ul key={key} className="list-disc pl-6 space-y-1">
                {val.map((item, i) => (
                  <li
                    key={i}
                    dangerouslySetInnerHTML={{
                      __html: formatBold(String(item)),
                    }}
                  />
                ))}
              </ul>
            );
          }
          if (key === "ol_list" && Array.isArray(val)) {
            return (
              <ol key={key} className="list-decimal pl-6 space-y-1">
                {val.map((item, i) => (
                  <li
                    key={i}
                    dangerouslySetInnerHTML={{
                      __html: formatBold(String(item)),
                    }}
                  />
                ))}
              </ol>
            );
          }
          if (key.match(/^\d+\.\d+$/) && typeof val === "string") {
            return (
              <p key={key}>
                <strong>{key}</strong>{" "}
                <span dangerouslySetInnerHTML={{ __html: formatBold(val) }} />
              </p>
            );
          }
          if (["p1", "p2", "p3"].includes(key) && typeof val === "string") {
            return (
              <p key={key}>
                <span dangerouslySetInnerHTML={{ __html: formatBold(val) }} />
              </p>
            );
          }
          if (typeof val === "string") {
            return (
              <p key={key}>
                <span dangerouslySetInnerHTML={{ __html: formatBold(val) }} />
              </p>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

const TermsDialog = ({
  open,
  onOpenChange,
  initialTab = "general",
  visibleTabs,
  highlightTabs,
  generalTermsAccepting = false,
}: TermsDialogProps) => {
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const labels = footerLabels(language);
  const termsContent = termsLabels(language);
  const [selectedTab, setSelectedTab] = useState<TabValue>(initialTab);
  const nativeSheetProps = useNativeExpandableSheetProps(open, {
    nativeClass: "native-legal-sheet native-sheet-expandable",
    className:
      "rounded-lg max-w-3xl max-h-[68vh] sm:max-h-[72vh] overflow-hidden flex flex-col p-0",
  });

  const allTabs: TabValue[] = [
    "general",
    "buyer-b2b",
    "buyer-consumer",
    "seller-business",
    "seller-consumer",
    "complaint",
    "pricing",
  ];
  const enabledTabs = useMemo(() => {
    const effectiveVisibleTabs =
      visibleTabs && visibleTabs.length > 0
        ? visibleTabs
        : generalTermsAccepting
        ? (["general"] as const)
        : allTabs;
    return effectiveVisibleTabs as TabValue[];
  }, [visibleTabs, generalTermsAccepting]);
  const highlighted = useMemo(() => {
    const effectiveHighlightTabs =
      highlightTabs && highlightTabs.length > 0
        ? highlightTabs
        : generalTermsAccepting
        ? (["general"] as const)
        : [];
    return new Set<TabValue>(effectiveHighlightTabs);
  }, [highlightTabs, generalTermsAccepting]);

  const wasOpenRef = useRef(false);

  // Reset to initialTab only when dialog opens
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const nextTab = enabledTabs.includes(initialTab)
        ? initialTab
        : enabledTabs[0] ?? "general";
      setSelectedTab(nextTab);
    }
    wasOpenRef.current = open;
  }, [open, initialTab, enabledTabs]);

  // If the active tab becomes hidden, move to the first visible one
  useEffect(() => {
    if (!enabledTabs.includes(selectedTab)) {
      setSelectedTab(enabledTabs[0] ?? "general");
    }
  }, [enabledTabs, selectedTab]);

  const triggerBaseClass =
    "text-[0.6rem] sm:text-xs whitespace-nowrap py-2.5 px-3 border-r border-muted-foreground/50 data-[state=active]:border-r-0";
  const getTriggerClass = (tab: TabValue, isLast: boolean) => {
    const highlight =
      highlighted.has(tab) && tab !== selectedTab
        ? " bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : highlighted.has(tab) && tab === selectedTab
        ? " bg-amber-500/20 text-amber-800 dark:text-amber-200"
        : "";
    const last = isLast ? " last:border-r-0" : "";
    return triggerBaseClass + last + highlight;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent {...nativeSheetProps} className={cn(nativeSheetProps.className)}>
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4">
          <DialogTitle>{labels.termsDialog.title}</DialogTitle>
        </DialogHeader>
        <Tabs
          value={selectedTab}
          onValueChange={(value) => setSelectedTab(value as TabValue)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-shrink-0 bg-muted">
            <div className="overflow-x-auto">
              <div className="pb-1">
                <TabsList className="p-1 px-2 bg-transparent rounded-md inline-flex w-max">
                  {enabledTabs.map((tab, idx) => {
                    const isLast = idx === enabledTabs.length - 1;
                    const label =
                      tab === "general"
                        ? labels.termsDialog?.tabs?.general ||
                          "Allmänna användarvillkor"
                        : tab === "buyer-b2b"
                        ? labels.termsDialog?.tabs?.buyerB2b ||
                          "Köparvillkor (Företag - B2B)"
                        : tab === "buyer-consumer"
                        ? labels.termsDialog?.tabs?.buyerConsumer ||
                          "Köparvillkor (Konsument)"
                        : tab === "seller-business"
                        ? labels.termsDialog?.tabs?.sellerBusiness ||
                          "Säljarvillkor (Företag)"
                        : tab === "seller-consumer"
                        ? labels.termsDialog?.tabs?.sellerConsumer ||
                          "Säljarvillkor (Konsument)"
                        : tab === "complaint"
                        ? labels.termsDialog?.tabs?.complaint ||
                          "Reklamationspolicy"
                        : labels.termsDialog?.tabs?.pricing || "Prislista";
                    return (
                      <TabsTrigger
                        key={tab}
                        value={tab}
                        className={getTriggerClass(tab, isLast)}
                      >
                        {label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </div>
          </div>

          {enabledTabs.includes("general") && (
            <TabsContent
              value="general"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.general as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.general || "Allmänna användarvillkor"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("buyer-b2b") && (
            <TabsContent
              value="buyer-b2b"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.buyerB2b as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.buyerB2b ||
                  "Köparvillkor (Företag - B2B)"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("buyer-consumer") && (
            <TabsContent
              value="buyer-consumer"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.buyerConsumer as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.buyerConsumer ||
                  "Köparvillkor (Konsument)"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("seller-business") && (
            <TabsContent
              value="seller-business"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.sellerBusiness as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.sellerBusiness ||
                  "Säljarvillkor (Företag)"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("seller-consumer") && (
            <TabsContent
              value="seller-consumer"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.sellerConsumer as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.sellerConsumer ||
                  "Säljarvillkor (Konsument)"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("complaint") && (
            <TabsContent
              value="complaint"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <StyledTermsTabContent
                data={termsContent?.complaint as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={
                  labels.termsDialog?.tabs?.complaint || "Reklamationspolicy"
                }
              />
            </TabsContent>
          )}

          {enabledTabs.includes("pricing") && (
            <TabsContent
              value="pricing"
              className="flex-1 overflow-y-auto mt-0 px-4 sm:px-6 pb-6 sm:pb-6 min-h-0"
            >
              <PricingTabContent
                data={termsContent?.pricing as TermsTabData | undefined}
                lastUpdated={labels.termsDialog?.lastUpdated || "Senast uppdaterad"}
                fallbackTitle={labels.termsDialog?.tabs?.pricing || "Prislista"}
              />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog;
